// Frontend logger — the single sanctioned boundary that touches `console`
// (Productive task: stop calling console.* directly across the app). The rest of
// the app must import `logger` / create a scoped `logger.child(...)`. Enforced by
// the `no-console: error` rule on frontend-app, with this file exempted in
// eslint.config.mjs.

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

const RANK: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 }

/** In dev, emit everything; in a production build, only warnings and errors. */
const DEFAULT_MIN_LEVEL: LogLevel = import.meta.env.DEV ? 'debug' : 'warn'

/**
 * Small leveled logger for the browser. Levels below the configured minimum are
 * dropped. Create scoped child loggers with {@link Logger.child} so messages carry
 * an `[atlas:scope]` prefix.
 */
export class Logger {
	constructor(
		private readonly scope?: string,
		private readonly minLevel: LogLevel = DEFAULT_MIN_LEVEL,
	) {}

	/** Derive a scoped logger, e.g. `logger.child('work-orders')`. */
	child(scope: string): Logger {
		return new Logger(this.scope ? `${this.scope}:${scope}` : scope, this.minLevel)
	}

	private emit(level: LogLevel, message: string, args: unknown[]): void {
		if (RANK[level] < RANK[this.minLevel]) return
		const prefix = this.scope ? `[atlas:${this.scope}]` : '[atlas]'
		console[level](`${prefix} ${message}`, ...args)
	}

	debug(message: string, ...args: unknown[]): void {
		this.emit('debug', message, args)
	}
	info(message: string, ...args: unknown[]): void {
		this.emit('info', message, args)
	}
	warn(message: string, ...args: unknown[]): void {
		this.emit('warn', message, args)
	}
	error(message: string, ...args: unknown[]): void {
		this.emit('error', message, args)
	}
}

/** Default app logger. Prefer `logger.child('feature')` for feature-scoped logs. */
export const logger = new Logger()
